import React, { useRef, useState } from 'react'
import { Progress, Spin } from 'antd'
import { CloudUploadOutlined, FileOutlined, CheckCircleOutlined } from '@ant-design/icons'
import * as JobFileApi from '../../api/jobfile.api'
import { openNotificationWithIcon } from '../../utils'
import mixpanel from 'mixpanel-browser'
import { FILE_SIZE_MSG } from '../../constants/index'

const IDLE = 'idle'
const SELECTED = 'selected'
const UPLOADING = 'uploading'

const allowedFileTypes = ['.doc', '.xls', '.xlsx', '.csv', '.pdf', '.jpg', '.jpeg', '.png', '.xlsm']
const imageTypes = ['.jpg', '.jpeg', '.png']

function getFileExt(name) {
  return name.slice(name.lastIndexOf('.')).toLowerCase()
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const FileUploadForm = ({ user, job, setFiles, socket }) => {
  const [uploadState, setUploadState] = useState(IDLE)
  const [selectedFile, setSelectedFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [progressCheck, setProgressCheck] = useState(0)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef(null)

  const processFile = (file) => {
    if (!file) return
    if (file.size > 100 * 1024 * 1024) {
      openNotificationWithIcon('error', 'Error', FILE_SIZE_MSG)
      return
    }
    const ext = getFileExt(file.name)
    if (!allowedFileTypes.includes(ext)) {
      openNotificationWithIcon('error', 'Error', `Invalid file type. Allowed: ${allowedFileTypes.join(', ')}`)
      return
    }
    setSelectedFile(file)
    setPreviewUrl(imageTypes.includes(ext) ? URL.createObjectURL(file) : null)
    setUploadState(SELECTED)
  }

  const handleFileChange = (e) => processFile(e.target.files[0])

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    processFile(e.dataTransfer.files[0])
  }

  const handleCancel = () => {
    setUploadState(IDLE)
    setSelectedFile(null)
    setPreviewUrl(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleUpload = async () => {
    if (!selectedFile) return
    try {
      setUploadState(UPLOADING)
      setProgressCheck(0)

      const formData = new FormData()
      formData.append('files', selectedFile)
      formData.append('user', user.id)
      formData.append('job', job.id)
      formData.append('uploadedBy', user.userType === 'customer' ? 'customer' : 'technician')

      // Optimistic update
      const optimisticFile = {
        fileName: selectedFile.name,
        fileUrl: previewUrl || '',
        jobFileData: {
          status: 'active',
          job: job.id,
          uploadedBy: user.userType,
          fileName: selectedFile.name,
          fileUrl: previewUrl || '',
        },
      }
      setFiles((prev) => [optimisticFile, ...prev])

      const response = await JobFileApi.uploadFile(formData, user.id)

      if (response.status) {
        setProgressCheck(50)
        socket.emit('refresh-file-list', { jobId: job.id, job, userType: user.userType, meeting: true })
        const jobIds = [job.id]
        if (job.is_transferred && job.transfer_reference_job) jobIds.push(job.transfer_reference_job)
        const updatedFiles = await JobFileApi.listOfUploadFile({ jobIds }, user.id)
        setFiles(updatedFiles.files)
        setProgressCheck(100)
        openNotificationWithIcon('success', 'Success', 'File uploaded successfully')
        mixpanel.identify(user.email)
        mixpanel.track('File uploaded successfully', { JobId: job.id, userId: user.id, userType: user.userType })
        setTimeout(() => {
          setUploadState(IDLE)
          setSelectedFile(null)
          setPreviewUrl(null)
          setProgressCheck(0)
        }, 1500)
      } else {
        const msg = response.errorDetails ? `Error: ${response.errorDetails.message}` : 'Unauthorized access to upload file'
        openNotificationWithIcon('error', 'Error', msg)
        setUploadState(SELECTED)
      }
    } catch (error) {
      console.error('Error uploading files:', error)
      openNotificationWithIcon('error', 'Error', 'File upload failed, please try again.')
      setUploadState(SELECTED)
    }
  }

  const isImage = previewUrl !== null

  return (
    <div className="fu-wrapper">
      <input
        ref={fileInputRef}
        type="file"
        accept={allowedFileTypes.join(',')}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {uploadState === IDLE && (
        <div
          className={`fu-dropzone${dragOver ? ' fu-dropzone--over' : ''}`}
          onClick={() => { if (fileInputRef.current) { fileInputRef.current.value = ''; fileInputRef.current.click() } }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <CloudUploadOutlined className="fu-upload-icon" />
          <p className="fu-drop-title">Click or drag a file here to upload</p>
          <p className="fu-drop-hint">{allowedFileTypes.join(' · ')} · Max 100 MB</p>
        </div>
      )}

      {uploadState !== IDLE && selectedFile && (
        <div className="fu-file-card">
          {isImage ? (
            <img src={previewUrl} alt="preview" className="fu-thumb" />
          ) : (
            <div className="fu-file-icon-wrap">
              <FileOutlined className="fu-file-icon" />
            </div>
          )}
          <div className="fu-file-info">
            <span className="fu-file-name">{selectedFile.name}</span>
            <span className="fu-file-size">{formatBytes(selectedFile.size)}</span>
            {uploadState === UPLOADING && (
              <Progress percent={progressCheck} size="small" strokeColor="#1bd4d5" style={{ marginTop: 6 }} />
            )}
            {uploadState === SELECTED && <span className="fu-file-ready">Ready to upload</span>}
          </div>
          {uploadState === UPLOADING ? (
            <Spin size="small" style={{ marginLeft: 8 }} />
          ) : (
            <CheckCircleOutlined className="fu-check-icon" />
          )}
        </div>
      )}

      {uploadState !== IDLE && (
        <div className="fu-actions">
          <button className="fu-btn fu-btn--ghost" onClick={handleCancel} disabled={uploadState === UPLOADING}>
            Cancel
          </button>
          <button className="fu-btn fu-btn--primary" onClick={handleUpload} disabled={uploadState === UPLOADING}>
            {uploadState === UPLOADING ? <><Spin size="small" />&nbsp;Uploading…</> : 'Upload File'}
          </button>
        </div>
      )}
    </div>
  )
}

export default FileUploadForm
